import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { FileText, Download, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const fmtEur = (n) => `€ ${Number(n || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function MijnFacturenPage() {
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api('get', '/api/platform/invoices')
      .then(r => setItems(r.data.invoices || []))
      .catch(() => toast.error('Facturen laden mislukt'))
      .finally(() => setLoading(false));
  }, []);

  const openInvoice = async (id) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/platform/invoices/${id}/html`;
    const token = localStorage.getItem('portal_token');
    try {
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const html = await r.text();
      const w = window.open('', '_blank');
      w.document.write(html);
      w.document.close();
    } catch (e) { toast.error('Factuur openen mislukt'); }
  };

  const downloadPdf = async (inv) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/platform/invoices/${inv.id}/pdf`;
    const token = localStorage.getItem('portal_token');
    try {
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) {
        const body = await r.json().catch(() => null);
        throw new Error(body?.detail || 'PDF niet beschikbaar');
      }
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${inv.invoice_number || 'factuur'}.pdf`;
      a.click();
    } catch (e) {
      toast.error(`PDF download mislukt: ${e.message}`);
    }
  };

  return (
    <div data-testid="mijn-facturen-page">
      <div className="mb-4">
        <h1 className="text-xl font-extrabold text-[#0f172a] flex items-center gap-2"><FileText size={18} className="text-[#D4A017]" /> Mijn facturen</h1>
        <p className="text-sm text-[#64748b] mt-1">Boekhoudklare facturen voor uw administratie.</p>
      </div>

      {loading ? <p className="text-center py-10 text-[#94a3b8]">Laden...</p>
        : items.length === 0
          ? <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
              <FileText size={32} className="text-[#D4A017] mx-auto mb-3" />
              <p className="font-bold text-[#0f172a]">Nog geen facturen</p>
              <p className="text-sm text-[#64748b] mt-1">Zodra u een betaling doet wordt automatisch een factuur gegenereerd.</p>
            </div>
          : (
            <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
              {items.map(inv => (
                <div key={inv.id} data-testid={`my-invoice-${inv.id}`} className="p-4 flex justify-between items-center gap-3 flex-wrap hover:bg-slate-50">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-bold text-[#0f172a]">{inv.invoice_number}</p>
                      <span className="bg-green-100 text-green-700 text-[10px] font-bold px-2 py-0.5 rounded uppercase">Betaald</span>
                    </div>
                    <p className="text-xs text-[#64748b] mt-1 flex items-center gap-1.5">
                      <Calendar size={11} /> {(inv.issue_date || '').slice(0, 10)} · {inv.lines?.[0]?.description}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-[#0f172a]">{fmtEur(inv.total_incl_vat)}</p>
                    <p className="text-[10px] text-[#94a3b8]">excl. btw: {fmtEur(inv.subtotal_ex_vat)}</p>
                  </div>
                  <button onClick={() => openInvoice(inv.id)} data-testid={`view-${inv.id}`} className="bg-white border border-slate-200 hover:border-[#D4A017] text-[#0f172a] text-xs font-bold px-3 py-2 rounded-lg flex items-center gap-1.5">
                    <FileText size={12} /> HTML
                  </button>
                  <button onClick={() => downloadPdf(inv)} data-testid={`pdf-${inv.id}`} className="bg-[#D4A017] hover:bg-[#B8860B] text-[#0f172a] text-xs font-bold px-3 py-2 rounded-lg flex items-center gap-1.5">
                    <Download size={12} /> PDF
                  </button>
                </div>
              ))}
            </div>
          )}
    </div>
  );
}
